import {
  ArrowRight,
  CalendarDays,
  Check,
  Clock,
  Landmark,
  Mountain,
  Plane,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Users,
  WalletCards,
  Waves,
  X,
} from 'lucide-react';
import { useEffect, useState, type CSSProperties, type FC } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl, type IntlShape } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, num, str } from '@/app/lib/a2ui/props.ts';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';

const tuneAccentStyle = {
  background:
    'linear-gradient(to right, hsl(var(--primary) / 0) 0%, hsl(var(--primary)) 50%, hsl(var(--primary) / 0) 100%)',
};

const messages = defineMessages({
  tripShape: {
    id: 'tripPlanComparison.tripShape',
    defaultMessage: 'Your trip shape',
    description: 'Heading above the compact summary of the traveller parameters used for the plans',
  },
  from: {
    id: 'tripPlanComparison.from',
    defaultMessage: 'From',
    description: 'Label for the departure city in the trip summary',
  },
  season: {
    id: 'tripPlanComparison.season',
    defaultMessage: 'Forecast',
    description: 'Label for the weather or season in the trip summary',
  },
  visa: {
    id: 'tripPlanComparison.visa',
    defaultMessage: 'Visa lane',
    description: 'Label for the visa constraint in the trip summary',
  },
  style: {
    id: 'tripPlanComparison.style',
    defaultMessage: 'Trip feel',
    description: 'Label for the travel style in the trip summary',
  },
  party: {
    id: 'tripPlanComparison.party',
    defaultMessage: 'Travelling with',
    description: 'Label for the optional family or traveller summary',
  },
  tuneTitle: {
    id: 'tripPlanComparison.tuneTitle',
    defaultMessage: 'Tune the trip',
    description: 'Heading above the budget and trip-length sliders',
  },
  budgetLabel: {
    id: 'tripPlanComparison.budgetLabel',
    defaultMessage: 'Budget per traveller',
    description: 'Label for the budget slider',
  },
  perDay: {
    id: 'tripPlanComparison.perDay',
    defaultMessage: '≈ {value}/day',
    description: 'Compact daily budget context shown beside the total budget slider value',
  },
  daysLabel: {
    id: 'tripPlanComparison.daysLabel',
    defaultMessage: 'Trip length',
    description: 'Label for the trip-length slider',
  },
  days: {
    id: 'tripPlanComparison.days',
    defaultMessage: '{count, plural, one {# day} other {# days}}',
    description: 'Formatted trip length value',
  },
  plansHeading: {
    id: 'tripPlanComparison.plansHeading',
    defaultMessage: 'Three ways to spend the same budget',
    description: 'Heading above the three equal-budget vacation-type cards',
  },
  planNumber: {
    id: 'tripPlanComparison.planNumber',
    defaultMessage: 'Plan {number}',
    description: 'Numbered label for one of the three vacation-type cards',
  },
  vacationWater: {
    id: 'tripPlanComparison.vacationWater',
    defaultMessage: 'Coastal / waterside / vibe',
    description: 'Label for the Coastal / Waterside / Vibe vacation type',
  },
  vacationCulture: {
    id: 'tripPlanComparison.vacationCulture',
    defaultMessage: 'Urban / cultural / heritage',
    description: 'Label for the Urban / Cultural / Heritage vacation type',
  },
  vacationNature: {
    id: 'tripPlanComparison.vacationNature',
    defaultMessage: 'Nature / scenic / outdoors',
    description: 'Label for the Nature / Scenic / Outdoors vacation type',
  },
  recommended: {
    id: 'tripPlanComparison.recommended',
    defaultMessage: 'Best fit',
    description: 'Badge for the plan most closely matching the traveller parameters',
  },
  stay: {
    id: 'tripPlanComparison.stay',
    defaultMessage: 'Stay',
    description: 'Label for the illustrative accommodation shape',
  },
  transport: {
    id: 'tripPlanComparison.transport',
    defaultMessage: 'Getting around',
    description: 'Label for the illustrative transport shape',
  },
  dining: {
    id: 'tripPlanComparison.dining',
    defaultMessage: 'Food',
    description: 'Label for the dynamically adjusted dining shape',
  },
  pace: {
    id: 'tripPlanComparison.pace',
    defaultMessage: 'Daily pace',
    description: 'Label for the dynamically adjusted daily pace',
  },
  bestFor: {
    id: 'tripPlanComparison.bestFor',
    defaultMessage: 'Best for',
    description: 'Label for why the plan fits the traveller',
  },
  includes: {
    id: 'tripPlanComparison.includes',
    defaultMessage: 'What fits this setting',
    description: 'Heading above the dynamically adjusted plan inclusions',
  },

  budgetLowerFallback: {
    id: 'tripPlanComparison.budgetLowerFallback',
    defaultMessage: 'Keep the essentials and trim premium extras.',
    description: 'Fallback note when the budget is below the starting setting',
  },
  budgetStandardFallback: {
    id: 'tripPlanComparison.budgetStandardFallback',
    defaultMessage: 'Keep the full mix of places, food and activities.',
    description: 'Fallback note at the starting budget setting',
  },
  budgetHigherFallback: {
    id: 'tripPlanComparison.budgetHigherFallback',
    defaultMessage: 'Add premium food and extra experiences.',
    description: 'Fallback note when the budget is above the starting setting',
  },
  daysShortFallback: {
    id: 'tripPlanComparison.daysShortFallback',
    defaultMessage: 'Keep the signature stops; there is less time for extras.',
    description: 'Fallback note when the trip is shorter than the starting setting',
  },
  daysStandardFallback: {
    id: 'tripPlanComparison.daysStandardFallback',
    defaultMessage: 'Keep the full rhythm across the trip.',
    description: 'Fallback note at the starting trip length',
  },
  daysLongFallback: {
    id: 'tripPlanComparison.daysLongFallback',
    defaultMessage: 'Add another layer of places, activities and food.',
    description: 'Fallback note when the trip is longer than the starting setting',
  },
  estimatedTotal: {
    id: 'tripPlanComparison.estimatedTotal',
    defaultMessage: 'Illustrative total per person',
    description: 'Label above the adjusted example total',
  },
  choose: {
    id: 'tripPlanComparison.choose',
    defaultMessage: 'Use this plan',
    description: 'Button selecting a plan and moving to the itinerary step',
  },
  dailyPlan: {
    id: 'tripPlanComparison.dailyPlan',
    defaultMessage: 'Daily plan',
    description: 'Button opening the compact sample day for one travel plan',
  },
  dailyPlanHeading: {
    id: 'tripPlanComparison.dailyPlanHeading',
    defaultMessage: '{label} · {plan}',
    description: 'Heading for the compact daily plan popup',
  },
  dailyPlanWindow: {
    id: 'tripPlanComparison.dailyPlanWindow',
    defaultMessage: '08:00–20:30 · Compact day-by-day rhythm',
    description: 'Time window shown in the daily plan popup',
  },
  dailyDay: {
    id: 'tripPlanComparison.dailyDay',
    defaultMessage: 'Day {day}',
    description: 'Day label in the daily plan popup',
  },
  remainingDay: {
    id: 'tripPlanComparison.remainingDay',
    defaultMessage: 'Day {start}: keep the rest of the trip open for recovery, local wandering and repeat favourites.',
    description: 'Summary for one remaining day after the detailed daily plan limit',
  },
  remainingDays: {
    id: 'tripPlanComparison.remainingDays',
    defaultMessage: 'Days {start}–{end}: keep the rest of the trip open for recovery, local wandering and repeat favourites.',
    description: 'Summary for remaining days after the detailed daily plan limit',
  },
  closeDailyPlan: {
    id: 'tripPlanComparison.closeDailyPlan',
    defaultMessage: 'Close daily plan',
    description: 'Accessible label for closing the daily plan popup',
  },
  dailyMorningOne: {
    id: 'tripPlanComparison.dailyMorningOne',
    defaultMessage: 'Start with a slow coffee and a walk through {place}, then make for {anchor}.',
    description: 'First varied morning activity in the compact daily plan',
  },
  dailyMorningTwo: {
    id: 'tripPlanComparison.dailyMorningTwo',
    defaultMessage: 'Pick up something local for breakfast before heading to {anchor}.',
    description: 'Second varied morning activity in the compact daily plan',
  },
  dailyMorningThree: {
    id: 'tripPlanComparison.dailyMorningThree',
    defaultMessage: 'Take the early light: a quiet wander or swim before {anchor}.',
    description: 'Third varied morning activity in the compact daily plan',
  },
  dailyAnchorOne: {
    id: 'tripPlanComparison.dailyAnchorOne',
    defaultMessage: 'Head out for {anchor}.',
    description: 'First varied daytime activity in the compact daily plan',
  },
  dailyAnchorTwo: {
    id: 'tripPlanComparison.dailyAnchorTwo',
    defaultMessage: 'Make {anchor} the main stop of the morning.',
    description: 'Second varied daytime activity in the compact daily plan',
  },
  dailyAnchorThree: {
    id: 'tripPlanComparison.dailyAnchorThree',
    defaultMessage: 'Take the scenic route to {anchor} and leave room to linger.',
    description: 'Third varied daytime activity in the compact daily plan',
  },
  dailyLunchOne: {
    id: 'tripPlanComparison.dailyLunchOne',
    defaultMessage: 'Take a long lunch with {dining}.',
    description: 'First varied lunch activity in the compact daily plan',
  },
  dailyLunchTwo: {
    id: 'tripPlanComparison.dailyLunchTwo',
    defaultMessage: 'Refuel with {dining}, then let the afternoon breathe.',
    description: 'Second varied lunch activity in the compact daily plan',
  },
  dailyLunchThree: {
    id: 'tripPlanComparison.dailyLunchThree',
    defaultMessage: 'Make lunch the event: {dining}.',
    description: 'Third varied lunch activity in the compact daily plan',
  },
  dailyAfternoonOne: {
    id: 'tripPlanComparison.dailyAfternoonOne',
    defaultMessage: 'Use the later light for {anchor}.',
    description: 'First varied afternoon activity in the compact daily plan',
  },
  dailyAfternoonTwo: {
    id: 'tripPlanComparison.dailyAfternoonTwo',
    defaultMessage: 'Keep the afternoon easy with {anchor}.',
    description: 'Second varied afternoon activity in the compact daily plan',
  },
  dailyAfternoonThree: {
    id: 'tripPlanComparison.dailyAfternoonThree',
    defaultMessage: 'Choose a shorter wander around {anchor} before sunset.',
    description: 'Third varied afternoon activity in the compact daily plan',
  },
  dailyEveningOne: {
    id: 'tripPlanComparison.dailyEveningOne',
    defaultMessage: 'Watch the light change near {anchor}, then settle in with {dining}.',
    description: 'First varied evening activity in the compact daily plan',
  },
  dailyEveningTwo: {
    id: 'tripPlanComparison.dailyEveningTwo',
    defaultMessage: 'End the day with a local table, a slow drink and {dining}.',
    description: 'Second varied evening activity in the compact daily plan',
  },
  dailyEveningThree: {
    id: 'tripPlanComparison.dailyEveningThree',
    defaultMessage: 'Follow the local buzz for sunset and {dining}.',
    description: 'Third varied evening activity in the compact daily plan',
  },
  emptyTitle: {
    id: 'tripPlanComparison.emptyTitle',
    defaultMessage: 'The plans need another look',
    description: 'Fallback heading when the comparison payload is incomplete',
  },
  emptyBody: {
    id: 'tripPlanComparison.emptyBody',
    defaultMessage: 'We could not load three grounded options yet. Ask us to try the comparison again.',
    description: 'Fallback body when the comparison payload is incomplete',
  },
});

type VacationKind = 'coastal' | 'urban' | 'nature';

const vacationKinds: VacationKind[] = ['coastal', 'urban', 'nature'];

type PlanOption = {
  id: string;
  kind: VacationKind;
  name: string;
  summary: string;
  bestFor: string;
  stay: string;
  transport: string;
  baseCost: number;
  includes: string[];
  budgetLowIncludes: string[];
  budgetHighIncludes: string[];
  shortDaysIncludes: string[];
  longDaysIncludes: string[];
  longStayLowIncludes: string[];
  dining: string;
  budgetLowDining: string;
  budgetHighDining: string;
  longStayLowDining: string;
  pace: string;
  shortDaysPace: string;
  longDaysPace: string;
  longStayLowPace: string;
  budgetLowNote: string;
  budgetHighNote: string;
  shortDaysNote: string;
  longDaysNote: string;
  longStayLowNote: string;
  recommended: boolean;
};

type PlanDefaults = Omit<PlanOption, 'id' | 'recommended' | 'baseCost'>;

function nonEmptyStrings(value: unknown, fallback: string[], limit = 4): string[] {
  const values = arr(value).map(str).filter((item) => item.length > 0).slice(0, limit);
  return values.length > 0 ? values : fallback.slice(0, limit);
}

function kindFor(value: unknown, index: number): VacationKind {
  const kind = str(value).toLowerCase();
  if (kind === 'coastal' || kind === 'waterside' || kind === 'water') {
    return 'coastal';
  }
  if (kind === 'urban' || kind === 'cultural' || kind === 'culture') {
    return 'urban';
  }
  if (kind === 'nature' || kind === 'scenic' || kind === 'outdoors') {
    return 'nature';
  }
  return vacationKinds[index] ?? 'nature';
}

function defaultPlan(kind: VacationKind, destinationName: string, city: string): PlanDefaults {
  const place = city ? `${city} in ${destinationName}` : destinationName;
  if (kind === 'coastal') {
    return {
      kind,
      name: 'Coastal flow',
      summary: `A waterside rhythm around ${place}: easy beach time, water walks, island flavor and lively evenings.`,
      bestFor: 'Beach time, swims and a social destination pulse',
      stay: 'Well-located guesthouse or relaxed beach hotel',
      transport: 'Walkable waterfront, local taxi and one boat or island transfer',
      includes: ['Waterfront walk and beach time', 'Local island or harbor outing', 'Snorkeling or an easy water activity', 'Town shops and a sunset evening'],
      budgetLowIncludes: ['Public beach and waterfront walk', 'Simple harbor or town outing', 'Self-guided swim and local shops'],
      budgetHighIncludes: ['Private boat or island excursion', 'Guided snorkeling and a sunset beach event'],
      shortDaysIncludes: ['Signature beach or waterfront stop', 'One water activity and a sunset walk'],
      longDaysIncludes: ['Second island or cove day', 'Beach party, concert or slower town afternoon'],
      longStayLowIncludes: ['Repeat a favourite beach or add a free seawall and town day'],
      dining: 'Casual waterfront cafés and one memorable local dinner',
      budgetLowDining: 'Market snacks, casual beach cafés and simple local meals',
      budgetHighDining: 'Sea-view fine dining, a cocktail stop and a special tasting menu',
      longStayLowDining: 'Self-catered breakfasts, market picnics and occasional local taverna meals',
      pace: 'Balanced beach, water and town days',
      shortDaysPace: 'Front-load the signature shore and water stops with lighter evenings',
      longDaysPace: 'Alternate active water days with unhurried beach and town days',
      longStayLowPace: 'Slow, local days: one paid anchor at a time with free beach and town days between',
      budgetLowNote: 'Keep the shore essentials and skip private excursions.',
      budgetHighNote: 'Add an exclusive boat outing, premium snorkeling and a livelier night.',
      shortDaysNote: 'Keep the signature shore stops; there is less room for island extras.',
      longDaysNote: 'Add another cove or island layer and more relaxed evenings.',
      longStayLowNote: 'Stretch the budget across the month with free beach days, local buses and only occasional paid outings.',
    };
  }
  if (kind === 'urban') {
    return {
      kind,
      name: 'City and culture',
      summary: `A city-led route through ${place}: heritage places, temples or monuments, museums, restaurants and a restorative spa pause.`,
      bestFor: 'Heritage, food, museums and a walkable city base',
      stay: 'Central boutique hotel or character guesthouse',
      transport: 'Walks, metro or local rides between heritage neighborhoods',
      includes: ['Signature city walk and heritage place', 'Temple, monument or museum visit', 'Restaurant-led food stop and neighborhood wander', 'Spa or slow cultural afternoon'],
      budgetLowIncludes: ['Self-guided heritage walk', 'One museum or monument', 'Neighborhood cafés and public gardens'],
      budgetHighIncludes: ['Expert heritage or food tour', 'Spa treatment and a fine-dining reservation'],
      shortDaysIncludes: ['Core heritage walk', 'One museum or monument and a signature meal'],
      longDaysIncludes: ['Second neighborhood or day trip', 'Extra museum, temple or spa afternoon'],
      longStayLowIncludes: ['Free architecture walk, public garden or neighbourhood market day'],
      dining: 'A mix of neighborhood restaurants and one polished dinner',
      budgetLowDining: 'Local cafés, market plates and one dependable neighborhood restaurant',
      budgetHighDining: 'Fine-dining restaurant, chef-led tasting and a spa tea break',
      longStayLowDining: 'Bakery breakfasts, market lunches, simple cooking and occasional neighborhood dinners',
      pace: 'Layered city days with a slower cultural reset',
      shortDaysPace: 'Cluster the central sights and keep transfers short',
      longDaysPace: 'Spread heritage, food and museum neighborhoods across gentler days',
      longStayLowPace: 'Use free neighbourhood walks and museums as the rhythm, with paid highlights spaced out',
      budgetLowNote: 'Prioritize public heritage places and casual local food.',
      budgetHighNote: 'Add a private cultural guide, spa time and a fine-dining night.',
      shortDaysNote: 'Keep the central heritage circuit; there is less time for outer districts.',
      longDaysNote: 'Add another neighborhood, museum or cultural day trip.',
      longStayLowNote: 'For a long stay, cook or use markets often and space paid museums, tours and spa time across the month.',
    };
  }
  return {
    kind,
    name: 'Scenic outdoors',
    summary: `A nature-first escape around ${place}: panoramic viewpoints, walking trails, iconic landmarks and an unusual local discovery.`,
    bestFor: 'Open-air days, big views and active exploration',
    stay: 'Quiet lodge, design inn or nature-edge guesthouse',
    transport: 'Scenic local rides, walking trails and one guided transfer',
    includes: ['Panoramic viewpoint and scenic walk', 'Signature trail or nature reserve', 'Iconic bridge, statue or landmark', 'Zoo, unusual habitat or exotic local stop'],
    budgetLowIncludes: ['Public viewpoint and marked walking trail', 'Iconic landmark or bridge', 'Picnic and a simple nature stop'],
    budgetHighIncludes: ['Private guide or scenic transfer', 'Exclusive lookout, wildlife or unusual habitat visit'],
    shortDaysIncludes: ['Signature viewpoint', 'One trail and the destination’s iconic outdoor landmark'],
    longDaysIncludes: ['Second trail or reserve', 'Zoo, exotic spot or slower scenic drive'],
    longStayLowIncludes: ['Repeat a local trail or add a free viewpoint, picnic and recovery day'],
    dining: 'Simple scenic cafés plus a lodge or neighborhood dinner',
    budgetLowDining: 'Picnic lunches, bakeries and simple local cafés near the trailheads',
    budgetHighDining: 'Scenic lodge dining, a destination restaurant and a celebratory drink',
    longStayLowDining: 'Picnic supplies, simple breakfasts and local cafés with occasional hearty dinners',
    pace: 'Active mornings balanced with scenic pauses',
    shortDaysPace: 'Choose one signature trail and one landmark per day',
    longDaysPace: 'Leave room for longer trails, wildlife and slower viewpoints',
    longStayLowPace: 'Alternate free scenic days and short walks with one paid or guided anchor at a time',
    budgetLowNote: 'Use public trails and viewpoints; keep guided extras optional.',
    budgetHighNote: 'Add a private guide, scenic transfer and an exclusive outdoor stop.',
    shortDaysNote: 'Keep the signature viewpoint and trail; save the outer spots for later.',
    longDaysNote: 'Add another trail, wildlife stop or scenic detour.',
    longStayLowNote: 'Make the month work with public trails, picnics and recovery days, saving paid guides for a few highlights.',
  };
}

function readPlans(value: unknown, destinationName: string, city: string, startingBudget: number): PlanOption[] {
  const plans: PlanOption[] = [];
  for (const [index, entry] of arr(value).entries()) {
    if (!isRecord(entry)) {
      continue;
    }
    const id = str(entry.id);
    const name = str(entry.name);
    const baseCost = num(entry.baseCost);
    if (!id || !name || baseCost === undefined || baseCost <= 0) {
      continue;
    }
    const kind = kindFor(entry.kind, index);
    const defaults = defaultPlan(kind, destinationName || 'your destination', city);
    plans.push({
      id,
      kind,
      name,
      summary: str(entry.summary) || defaults.summary,
      bestFor: str(entry.bestFor) || defaults.bestFor,
      stay: str(entry.stay) || defaults.stay,
      transport: str(entry.transport) || defaults.transport,
      baseCost,
      includes: nonEmptyStrings(entry.includes, defaults.includes),
      budgetLowIncludes: nonEmptyStrings(entry.budgetLowIncludes, defaults.budgetLowIncludes),
      budgetHighIncludes: nonEmptyStrings(entry.budgetHighIncludes, defaults.budgetHighIncludes),
      shortDaysIncludes: nonEmptyStrings(entry.shortDaysIncludes, defaults.shortDaysIncludes),
      longDaysIncludes: nonEmptyStrings(entry.longDaysIncludes, defaults.longDaysIncludes),
      longStayLowIncludes: nonEmptyStrings(entry.longStayLowIncludes, defaults.longStayLowIncludes, 3),
      dining: str(entry.dining) || defaults.dining,
      budgetLowDining: str(entry.budgetLowDining) || defaults.budgetLowDining,
      budgetHighDining: str(entry.budgetHighDining) || defaults.budgetHighDining,
      longStayLowDining: str(entry.longStayLowDining) || defaults.longStayLowDining,
      pace: str(entry.pace) || defaults.pace,
      shortDaysPace: str(entry.shortDaysPace) || defaults.shortDaysPace,
      longDaysPace: str(entry.longDaysPace) || defaults.longDaysPace,
      longStayLowPace: str(entry.longStayLowPace) || defaults.longStayLowPace,
      budgetLowNote: str(entry.budgetLowNote) || defaults.budgetLowNote,
      budgetHighNote: str(entry.budgetHighNote) || defaults.budgetHighNote,
      shortDaysNote: str(entry.shortDaysNote) || defaults.shortDaysNote,
      longDaysNote: str(entry.longDaysNote) || defaults.longDaysNote,
      longStayLowNote: str(entry.longStayLowNote) || defaults.longStayLowNote,
      recommended: entry.recommended === true,
    });
  }
  if (plans.length === 3) {
    return plans;
  }
  return vacationKinds.map((kind, index) => {
    const defaults = defaultPlan(kind, destinationName || 'your destination', city);
    return {
      ...defaults,
      id: `demo-${kind}`,
      baseCost: startingBudget,
      recommended: index === 1,
    };
  });
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function roundToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function rangeProgress(value: number, minimum: number, maximum: number): number {
  if (maximum <= minimum) {
    return 0;
  }
  return clamp(((value - minimum) / (maximum - minimum)) * 100, 0, 100);
}

function rangeStyle(progress: number): CSSProperties {
  return { '--range-progress': `${progress}%` } as CSSProperties;
}

type AdjustedPlan = PlanOption & {
  adjustedIncludes: string[];
  adjustedDining: string;
  adjustedPace: string;
  budgetNote: string;
  daysNote: string;
};

function uniqueItems(items: string[], limit = 4): string[] {
  return Array.from(new Set(items.filter((item) => item.length > 0))).slice(0, limit);
}

function adjustPlan(plan: PlanOption, budget: number, baselineBudget: number, days: number, baselineDays: number): AdjustedPlan {
  const totalBudgetRatio = budget / Math.max(1, baselineBudget);
  const dailyBudget = budget / Math.max(1, days);
  const baselineDailyBudget = baselineBudget / Math.max(1, baselineDays);
  const dailyBudgetRatio = dailyBudget / Math.max(1, baselineDailyBudget);
  const daysRatio = days / Math.max(1, baselineDays);
  const isLowerBudget = dailyBudgetRatio < 0.85 || (totalBudgetRatio < 0.8 && dailyBudgetRatio < 1.05);
  const isHigherBudget = dailyBudgetRatio > 1.15 || (totalBudgetRatio > 1.35 && dailyBudgetRatio > 1);
  const isLongTrip = daysRatio > 1.35 || days >= 21;
  const isTightLongTrip = isLongTrip && dailyBudgetRatio < 0.85;

  let adjustedIncludes = isLowerBudget
    ? uniqueItems(plan.budgetLowIncludes, 3)
    : isHigherBudget
      ? uniqueItems([...plan.includes, ...plan.budgetHighIncludes], 5)
      : uniqueItems(plan.includes, 4);
  let adjustedDining = isLowerBudget
    ? plan.budgetLowDining
    : isHigherBudget
      ? plan.budgetHighDining
      : plan.dining;
  let adjustedPace = plan.pace;
  let budgetNote = isLowerBudget
    ? plan.budgetLowNote
    : isHigherBudget
      ? plan.budgetHighNote
      : 'Keep the full mix of places, food and activities.';
  let daysNote = days < baselineDays - 1
    ? plan.shortDaysNote
    : days > baselineDays + 1
      ? plan.longDaysNote
      : 'Keep the full rhythm across the trip.';

  if (isTightLongTrip) {
    adjustedIncludes = uniqueItems([...plan.budgetLowIncludes.slice(0, 2), ...plan.longStayLowIncludes], 3);
    adjustedDining = plan.longStayLowDining;
    adjustedPace = plan.longStayLowPace;
    budgetNote = plan.longStayLowNote;
    daysNote = plan.longStayLowNote;
  } else if (days < baselineDays - 1) {
    adjustedIncludes = uniqueItems([...adjustedIncludes.slice(0, 1), ...plan.shortDaysIncludes], 3);
    adjustedPace = plan.shortDaysPace;
  } else if (days > baselineDays + 1) {
    adjustedIncludes = uniqueItems(
      [...adjustedIncludes, ...(isLowerBudget ? plan.longStayLowIncludes : plan.longDaysIncludes)],
      isHigherBudget ? 5 : 4,
    );
    adjustedPace = isLowerBudget ? plan.longStayLowPace : plan.longDaysPace;
  }

  return {
    ...plan,
    adjustedIncludes,
    adjustedDining,
    adjustedPace,
    budgetNote,
    daysNote,
  };
}

type BookingItineraryDay = {
  day: number;
  slots: { time: string; detail: string }[];
};

function buildBookingItinerary(
  adjustedPlan: AdjustedPlan,
  days: number,
  city: string,
  destinationName: string,
  intl: IntlShape,
): BookingItineraryDay[] {
  return Array.from({ length: Math.min(days, 12) }, (_, index) => {
    const anchors = adjustedPlan.adjustedIncludes.length > 0
      ? adjustedPlan.adjustedIncludes
      : adjustedPlan.includes;
    const primaryAnchor = anchors[index % anchors.length] ?? adjustedPlan.bestFor;
    const secondaryAnchor = anchors[(index + 1) % anchors.length] ?? adjustedPlan.adjustedPace;
    const place = city || destinationName || 'your base';
    return {
      day: index + 1,
      slots: [
        {
          time: '08:00',
          detail: intl.formatMessage(
            [messages.dailyMorningOne, messages.dailyMorningTwo, messages.dailyMorningThree][index % 3],
            { place, anchor: primaryAnchor },
          ),
        },
        {
          time: '10:00',
          detail: intl.formatMessage(
            [messages.dailyAnchorOne, messages.dailyAnchorTwo, messages.dailyAnchorThree][index % 3],
            { anchor: primaryAnchor },
          ),
        },
        {
          time: '13:00',
          detail: intl.formatMessage(
            [messages.dailyLunchOne, messages.dailyLunchTwo, messages.dailyLunchThree][index % 3],
            { dining: adjustedPlan.adjustedDining },
          ),
        },
        {
          time: '16:30',
          detail: intl.formatMessage(
            [messages.dailyAfternoonOne, messages.dailyAfternoonTwo, messages.dailyAfternoonThree][index % 3],
            { anchor: secondaryAnchor },
          ),
        },
        {
          time: '19:30',
          detail: intl.formatMessage(
            [messages.dailyEveningOne, messages.dailyEveningTwo, messages.dailyEveningThree][index % 3],
            { anchor: secondaryAnchor, dining: adjustedPlan.adjustedDining },
          ),
        },
      ],
    };
  });
}

export const TripPlanComparison: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const title = str(node.props.title);
  const subtitle = str(node.props.subtitle);
  const destinationName = str(node.props.destinationName);
  const city = str(node.props.city);
  const origin = str(node.props.origin);
  const style = str(node.props.style);
  const season = str(node.props.season);
  const visa = str(node.props.visa);
  const party = str(node.props.party);
  const rawBudget = num(node.props.budget);
  const rawDays = num(node.props.days);
  const baselineBudget = Math.max(100, rawBudget ?? 2000);
  const baselineDays = clamp(Math.round(rawDays ?? 7), 2, 60);
  const plans = readPlans(node.props.plans, destinationName, city, baselineBudget);

  const budgetMinimum = Math.max(100, Math.floor((baselineBudget * 0.55) / 50) * 50);
  const budgetMaximum = Math.max(
    budgetMinimum + 100,
    Math.ceil((baselineBudget * 1.75) / 50) * 50,
  );
  const daysMinimum = Math.min(3, baselineDays);
  const daysMaximum = Math.max(30, baselineDays);
  const [budget, setBudget] = useState(() => clamp(roundToStep(baselineBudget, 50), budgetMinimum, budgetMaximum));
  const [days, setDays] = useState(() => baselineDays);

  useEffect(() => {
    setBudget(clamp(roundToStep(baselineBudget, 50), budgetMinimum, budgetMaximum));
    setDays(baselineDays);
  }, [baselineBudget, baselineDays, budgetMaximum, budgetMinimum]);

  const [openDailyPlanId, setOpenDailyPlanId] = useState<string | null>(null);

  const formatCurrency = (value: number): string =>
    intl.formatNumber(Math.max(0, Math.round(value)), {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  const formatDays = (value: number): string => intl.formatMessage(messages.days, { count: value });
  const budgetProgress = rangeProgress(budget, budgetMinimum, budgetMaximum);
  const daysProgress = rangeProgress(days, daysMinimum, daysMaximum);
  const dailyBudget = budget / Math.max(1, days);
  const formatPlanCost = (): number => Math.max(50, roundToStep(budget, 10));
  const dailyPlanSource = plans.find((plan) => plan.id === openDailyPlanId);
  const adjustedDailyPlan = dailyPlanSource
    ? adjustPlan(dailyPlanSource, budget, baselineBudget, days, baselineDays)
    : null;
  const dailyPlanDays = adjustedDailyPlan
    ? buildBookingItinerary(adjustedDailyPlan, days, city, destinationName, intl)
    : [];

  const selectPlan = (plan: AdjustedPlan): void => {
    dispatch?.('selectPlan', {
      destinationName,
      city,
      planId: plan.id,
      planName: plan.name,
      planType: plan.kind,
      budget,
      days,
      estimatedCost: formatPlanCost(),
      budgetMix: plan.budgetNote,
      daysMix: plan.daysNote,
      dining: plan.adjustedDining,
      pace: plan.adjustedPace,
      itinerary: buildBookingItinerary(plan, days, city, destinationName, intl),
      origin,
      season,
      visa,
      style,
      party,
    });
  };

  if (plans.length < 3) {
    return (
      <section
        className="travel-step-four flex w-full flex-col gap-4 rounded-[15px] border border-border bg-card p-6 text-foreground sm:p-8"
        data-wayfarer-step="4"
        data-wayfarer-step-available-through="4"
      >
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          {intl.formatMessage(messages.emptyTitle)}
        </h1>
        <p className="max-w-[38rem] text-base leading-relaxed text-muted-foreground">
          {intl.formatMessage(messages.emptyBody)}
        </p>
      </section>
    );
  }

  return (
    <>
      <div
        className="travel-step-four flex w-full flex-col gap-5 text-foreground"
      data-wayfarer-step="4"
      data-wayfarer-step-available-through="4"
    >
      <header className="animate-fadeUp motion-reduce:animate-none">
        <h1 className="w-full max-w-none text-balance text-3xl font-bold leading-tight tracking-tighter text-foreground sm:text-4xl">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-3 w-full max-w-none text-base leading-relaxed text-muted-foreground">{subtitle}</p>
        ) : null}
        <div className="mt-5 flex flex-wrap gap-2" aria-label={intl.formatMessage(messages.tripShape)}>
          {[
            [Plane, intl.formatMessage(messages.from), origin],
            [CalendarDays, intl.formatMessage(messages.season), season],
            [ShieldCheck, intl.formatMessage(messages.visa), visa],
            [Sparkles, intl.formatMessage(messages.style), style],
            [Users, intl.formatMessage(messages.party), party],
          ].map(([Icon, label, value]) => {
            const Mark = Icon as typeof Plane;
            return value ? (
              <span
                key={label as string}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground"
              >
                <Mark className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                <span className="font-semibold text-foreground">{label as string}:</span>
                <span className="min-w-0 truncate">{value as string}</span>
              </span>
            ) : null;
          })}
        </div>
      </header>

      <section
        className="relative overflow-hidden rounded-full border border-travel-shortlist-border border-t-transparent bg-travel-shortlist-card"
        aria-labelledby="trip-plan-tune"
      >
        <span
          className="pointer-events-none absolute inset-x-5 top-0 z-10 h-px rounded-full sm:inset-x-6"
          style={tuneAccentStyle}
          aria-hidden="true"
        />
        <div className="flex flex-row items-stretch">
          <div className="flex shrink-0 items-center gap-2.5 border-e border-travel-shortlist-border px-4 py-3 whitespace-nowrap">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            </span>
            <h2 id="trip-plan-tune" className="whitespace-nowrap text-sm font-bold tracking-tight text-travel-shortlist-foreground">
              {intl.formatMessage(messages.tuneTitle)}
            </h2>
          </div>

          <div className="grid min-w-0 flex-1 grid-cols-2 divide-x divide-travel-shortlist-border">
            <label
              className="group min-w-0 px-4 py-3 transition-colors hover:bg-travel-shortlist-background"
              htmlFor="trip-plan-budget"
            >
              <span className="flex items-center justify-between gap-3 whitespace-nowrap text-xs font-bold text-travel-shortlist-foreground">
                <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                  <WalletCards className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                  <span className="truncate">{intl.formatMessage(messages.budgetLabel)}</span>
                </span>
                <span className="flex shrink-0 items-baseline gap-1.5 tabular-nums text-primary">
                  <span>{formatCurrency(budget)}</span>
                  <span className="text-[11px] font-semibold text-travel-shortlist-muted">
                    {intl.formatMessage(messages.perDay, { value: formatCurrency(dailyBudget) })}
                  </span>
                </span>
              </span>
              <input
                id="trip-plan-budget"
                type="range"
                min={budgetMinimum}
                max={budgetMaximum}
                step={50}
                value={budget}
                onChange={(event) => setBudget(Number(event.target.value))}
                aria-valuetext={`${formatCurrency(budget)}, ${formatCurrency(dailyBudget)} per day`}
                style={rangeStyle(budgetProgress)}
                className="mt-2.5 h-1.5 w-full cursor-pointer accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              />
            </label>

            <label
              className="group min-w-0 px-4 py-3 transition-colors hover:bg-travel-shortlist-background"
              htmlFor="trip-plan-days"
            >
              <span className="flex items-center justify-between gap-3 whitespace-nowrap text-xs font-bold text-travel-shortlist-foreground">
                <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                  <CalendarDays className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                  <span>{intl.formatMessage(messages.daysLabel)}</span>
                </span>
                <span className="shrink-0 tabular-nums text-primary">{formatDays(days)}</span>
              </span>
              <input
                id="trip-plan-days"
                type="range"
                min={daysMinimum}
                max={daysMaximum}
                step={1}
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
                aria-valuetext={formatDays(days)}
                style={rangeStyle(daysProgress)}
                className="mt-2.5 h-1.5 w-full cursor-pointer accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              />
            </label>
          </div>
        </div>
      </section>

      <section aria-labelledby="trip-plan-options">
        <h2 id="trip-plan-options" className="text-xl font-bold tracking-tight text-foreground">
          {intl.formatMessage(messages.plansHeading)}
        </h2>

        <div className="mt-3 grid gap-3 lg:grid-cols-3">
          {plans.map((plan, index) => {
            const adjustedPlan = adjustPlan(plan, budget, baselineBudget, days, baselineDays);
            const estimatedCost = formatPlanCost();
            const recommended = plan.recommended || (!plans.some((item) => item.recommended) && index === 1);
            const kindLabel = intl.formatMessage(
              {
                coastal: messages.vacationWater,
                urban: messages.vacationCulture,
                nature: messages.vacationNature,
              }[plan.kind],
            );
            const KindIcon = plan.kind === 'coastal' ? Waves : plan.kind === 'urban' ? Landmark : Mountain;
            return (
              <article
                key={plan.id}
                data-recommended={recommended}
                className="flex min-w-0 flex-col rounded-[15px] border border-border bg-card p-4 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/45 hover:shadow-[0_8px_20px_hsl(var(--primary)/0.12)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                      <KindIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {intl.formatMessage(messages.planNumber, { number: index + 1 })}
                    </span>
                    <span className="ml-2 inline-flex max-w-full items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                      {kindLabel}
                    </span>
                    {recommended ? (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        {intl.formatMessage(messages.recommended)}
                      </span>
                    ) : null}
                    <h3 className="mt-2 break-words text-lg font-bold tracking-tight text-foreground">
                      {plan.name}
                    </h3>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
                    <KindIcon className="h-5 w-5" />
                  </span>
                </div>

                {plan.summary ? (
                  <p className="mt-2 max-h-[4.25rem] overflow-hidden text-sm leading-relaxed text-muted-foreground">{plan.summary}</p>
                ) : null}

                <div className="mt-3 grid gap-1.5 border-y border-border py-2.5 text-sm leading-relaxed">
                  <p className="min-w-0 break-words text-muted-foreground">
                    <span className="font-semibold text-foreground">{intl.formatMessage(messages.dining)}:</span>{' '}
                    {adjustedPlan.adjustedDining}
                  </p>
                  <p className="min-w-0 break-words text-muted-foreground">
                    <span className="font-semibold text-foreground">{intl.formatMessage(messages.pace)}:</span>{' '}
                    {adjustedPlan.adjustedPace}
                  </p>
                </div>

                {adjustedPlan.adjustedIncludes.length > 0 ? (
                  <div className="mt-3 min-w-0">
                    <p className="text-sm font-bold uppercase tracking-caps text-muted-foreground">
                      {intl.formatMessage(messages.includes)}
                    </p>
                    <ul className="mt-2 space-y-2">
                      {adjustedPlan.adjustedIncludes.map((include, includeIndex) => (
                        <li key={`${plan.id}-${includeIndex}-${include}`} className="flex min-w-0 gap-2 text-sm leading-relaxed text-foreground">
                          <Check className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                          <span className="min-w-0 break-words">{include}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="mt-auto pt-4">
                  <p className="text-xs text-muted-foreground">{intl.formatMessage(messages.estimatedTotal)}</p>
                  <p className="mt-1 text-xl font-bold tabular-nums tracking-tight text-foreground">
                    {formatCurrency(estimatedCost)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatDays(days)} · {formatCurrency(budget)} total · {intl.formatMessage(messages.perDay, { value: formatCurrency(dailyBudget) })}
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setOpenDailyPlanId(plan.id)}
                      className="flex min-w-0 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-input bg-foreground px-2 py-2.5 text-sm font-bold text-background transition duration-150 [touch-action:manipulation] hover:bg-foreground/90 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                    >
                      <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span>{intl.formatMessage(messages.dailyPlan)}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => selectPlan(adjustedPlan)}
                      className={`flex min-w-0 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-input px-2 py-2.5 text-sm font-bold transition duration-150 [touch-action:manipulation] active:scale-[0.99] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                        recommended
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'border border-primary/35 bg-background text-primary hover:border-primary hover:bg-primary/10'
                      }`}
                    >
                      <span>{intl.formatMessage(messages.choose)}</span>
                      <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
      {adjustedDailyPlan
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-travel-shortlist-foreground/55 p-4"
              role="presentation"
              onMouseDown={() => setOpenDailyPlanId(null)}
            >
              <div
                className="flex max-h-[calc(100vh-2rem)] w-full max-w-[1240px] flex-col overflow-hidden rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-card text-travel-shortlist-foreground tracking-[0.3px] shadow-2xl"
                role="dialog"
                aria-modal="true"
                data-wayfarer-daily-plan="true"
                aria-labelledby={`trip-daily-plan-${adjustedDailyPlan.id}`}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-4 px-4 pb-0 pt-4 sm:px-6 sm:pt-5">
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.3px] text-primary">
                      {intl.formatMessage(messages.dailyPlan)}
                    </p>
                    <h2
                      id={`trip-daily-plan-${adjustedDailyPlan.id}`}
                      className="mt-1 break-words text-2xl font-bold tracking-[0.3px] text-travel-shortlist-foreground"
                    >
                      {intl.formatMessage(messages.dailyPlanHeading, {
                        label: intl.formatMessage(messages.dailyPlan),
                        plan: adjustedDailyPlan.name,
                      })}
                    </h2>
                    <p className="mt-1 text-sm text-travel-shortlist-muted">
                      {formatDays(days)} · {city || destinationName}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-travel-shortlist-border bg-travel-shortlist-card text-travel-shortlist-foreground transition-colors hover:border-travel-shortlist-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                    aria-label={intl.formatMessage(messages.closeDailyPlan)}
                    onClick={() => setOpenDailyPlanId(null)}
                  >
                    <X className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
                  <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-travel-shortlist-muted">
                    <Clock className="h-4 w-4 shrink-0 text-travel-shortlist-foreground" aria-hidden="true" />
                    <span>{intl.formatMessage(messages.dailyPlanWindow)}</span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {dailyPlanDays.map((day) => (
                      <article
                        key={`${adjustedDailyPlan.id}-day-${day.day}`}
                        className="min-w-0 rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-background p-3.5"
                      >
                        <div className="flex items-center justify-between gap-3 border-b border-travel-shortlist-border pb-2">
                          <h3 className="flex items-center gap-2 text-lg font-bold tracking-[0.3px] text-travel-shortlist-foreground">
                            <CalendarDays className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                            <span>{intl.formatMessage(messages.dailyDay, { day: day.day })}</span>
                          </h3>
                          <span className="text-xs font-semibold tabular-nums text-travel-shortlist-foreground">08:00–20:30</span>
                        </div>
                        <div className="mt-2 grid gap-2">
                          {day.slots.map((slot, slotIndex) => (
                            <div
                              key={`${adjustedDailyPlan.id}-day-${day.day}-${slot.time}`}
                              className={`grid grid-cols-[3.75rem_minmax(0,1fr)] gap-2 text-sm ${slotIndex > 0 ? 'border-t border-travel-shortlist-border pt-2' : ''}`}
                            >
                              <span className="font-bold tabular-nums text-travel-shortlist-foreground">{slot.time}</span>
                              <div className="min-w-0">
                                <p className="break-words leading-relaxed text-travel-shortlist-foreground">{slot.detail}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                  {days > dailyPlanDays.length ? (
                    <div className="mt-3 rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-background px-4 py-3">
                      <p className="text-sm font-bold text-travel-shortlist-foreground">
                        {days - dailyPlanDays.length === 1
                          ? intl.formatMessage(messages.remainingDay, {
                              start: dailyPlanDays.length + 1,
                            })
                          : intl.formatMessage(messages.remainingDays, {
                              start: dailyPlanDays.length + 1,
                              end: days,
                            })}
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-travel-shortlist-muted">
                        {adjustedDailyPlan.adjustedPace}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};
